import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    Mail,
    ChevronDown,
    Chrome,
    Zap,
    Shield,
    RefreshCw,
    Search,
    CreditCard,
    Trash2,
    Bug,
    Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Support page — public URL: brainsync.pro/support
// Purpose: satisfies Chrome Web Store "Support URL" requirement and
// serves as a first-line self-service help hub. Deliberately terse and
// scannable — reviewers should be able to see 5+ common answers above
// the fold on a laptop.
//
// Structure:
//   1. Contact hero (email + expected response time)
//   2. Getting Started (3 quick bullets)
//   3. FAQ accordion (8 items, most-asked first)
//   4. Legal / links footer
//
// SEO/GEO: adds `FAQPage` structured data so Google can surface answers
// directly in SERP snippets.

const FAQS = [
    {
        icon: Chrome,
        q: "The Chrome extension isn't saving my page. What now?",
        a: (
            <>
                <p>Three things to check, in order:</p>
                <ol>
                    <li>Make sure you're <strong>signed in</strong> at <a href="https://brainsync.pro" className="text-emerald-700 hover:underline">brainsync.pro</a> in the same Chrome profile. The extension reuses your website session — no separate login.</li>
                    <li>Click the BrainSync icon and confirm the URL shown in the popup matches the page you're on. If it's blank, refresh the page and try again.</li>
                    <li>Check that you haven't hit the free-tier limit (20 AI-processed saves per month). Upgrade in <Link to="/account/billing" className="text-emerald-700 hover:underline">Billing</Link> or wait for the monthly reset.</li>
                </ol>
                <p>Still stuck? Email us at <a href="mailto:support@brainsync.pro" className="text-emerald-700 hover:underline">support@brainsync.pro</a> with the URL you tried to save.</p>
            </>
        ),
    },
    {
        icon: Zap,
        q: "How long does it take for a new bookmark to be processed?",
        a: (
            <p>
                Most articles finish in <strong>10–30 seconds</strong>. Videos, podcasts, and long-form content that need audio transcription can take <strong>1–3 minutes</strong>, occasionally longer for hour-long videos. If a bookmark is stuck on "Processing" for more than 10 minutes, that means something failed silently — reload the dashboard and it'll either show up or be marked as an error you can retry.
            </p>
        ),
    },
    {
        icon: Search,
        q: "How does \"Ask BrainSync\" search work?",
        a: (
            <p>
                Ask BrainSync is a natural-language search across everything <em>you&apos;ve</em> saved. It uses your bookmarks as the knowledge base — never the general web, never other users&apos; data — so answers cite the specific pages you bookmarked. Great for questions like &quot;which recipes did I save with lemon?&quot; or &quot;what did that Karpathy talk say about attention?&quot;. Free tier includes 10 questions per month, Plus includes 50, Pro includes 150.
            </p>
        ),
    },
    {
        icon: CreditCard,
        q: "How do I upgrade, downgrade, or cancel Pro?",
        a: (
            <p>
                Head to <Link to="/account/billing" className="text-emerald-700 hover:underline">Billing</Link> on your dashboard. You can upgrade, switch plans, or cancel any time — cancellations take effect at the end of your current period, and your data stays intact regardless. If you don't see the changes reflected within 5 minutes, refresh and check again. Still off? Email <a href="mailto:support@brainsync.pro" className="text-emerald-700 hover:underline">support@brainsync.pro</a>.
            </p>
        ),
    },
    {
        icon: Shield,
        q: "Is my data private? Who can see my bookmarks?",
        a: (
            <>
                <p>
                    Your bookmarks are private by default — nobody but you can see them. The only exceptions are:
                </p>
                <ul>
                    <li><strong>Public profiles</strong> — if you opt in at <Link to="/settings" className="text-emerald-700 hover:underline">Settings</Link>, your username page at <code>brainsync.pro/&lt;you&gt;</code> becomes visible. You choose which categories are public.</li>
                    <li><strong>Shared categories</strong> — anyone with a share link you send can view that specific category. Revoke any time.</li>
                </ul>
                <p>
                    Full details in our <Link to="/privacy" className="text-emerald-700 hover:underline">Privacy Policy</Link>. Data is never sold, never used to train external models.
                </p>
            </>
        ),
    },
    {
        icon: RefreshCw,
        q: "I saved a video but the transcript is missing or wrong. Why?",
        a: (
            <p>
                A few reasons: (1) some platforms (Instagram, X) actively block us from downloading their media, so we fall back to the visible caption and page metadata; (2) very quiet or heavily-music-only videos confuse Whisper; (3) videos over ~2 hours are truncated. If a specific video should have worked and didn't, forward the URL to <a href="mailto:support@brainsync.pro" className="text-emerald-700 hover:underline">support@brainsync.pro</a> — we log every failure and use them to prioritize fixes.
            </p>
        ),
    },
    {
        icon: Trash2,
        q: "How do I delete my account and all my data?",
        a: (
            <p>
                Go to <Link to="/settings" className="text-emerald-700 hover:underline">Settings → Danger Zone</Link> and hit "Delete account". This immediately removes all your bookmarks, categories, chat sessions, and profile data from our production database. Backups roll off within 30 days. If you'd rather export first, ping us at <a href="mailto:support@brainsync.pro" className="text-emerald-700 hover:underline">support@brainsync.pro</a> and we'll send you a JSON dump before you delete.
            </p>
        ),
    },
    {
        icon: Bug,
        q: "I found a bug or have a feature request.",
        a: (
            <p>
                Please tell us — we read every message. Email <a href="mailto:support@brainsync.pro" className="text-emerald-700 hover:underline">support@brainsync.pro</a> with a screenshot if the bug is visual, or a URL if a specific link caused it. Feature requests get logged into our public roadmap; the ones most people ask for get built first.
            </p>
        ),
    },
];

function FaqItem({ item, isOpen, onToggle, index }) {
    const Icon = item.icon;
    return (
        <div
            className="border-b border-zinc-200 last:border-b-0"
            data-testid={`support-faq-item-${index}`}
        >
            <button
                onClick={onToggle}
                className="w-full flex items-start gap-4 py-5 text-left hover:bg-zinc-50/50 transition-colors px-2 -mx-2 rounded-lg"
                data-testid={`support-faq-toggle-${index}`}
                aria-expanded={isOpen}
            >
                <span className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 font-medium text-[15px] text-zinc-900 leading-snug pt-1.5">
                    {item.q}
                </span>
                <ChevronDown
                    className={`w-5 h-5 text-zinc-400 flex-shrink-0 mt-2 transition-transform ${isOpen ? 'rotate-180 text-emerald-700' : ''}`}
                />
            </button>
            {isOpen && (
                <div
                    className="pb-6 pl-14 pr-4 text-sm text-zinc-600 leading-relaxed space-y-3 [&_p]:leading-relaxed [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:ml-4 [&_ul]:ml-4 [&_ol>li]:pl-1 [&_ul>li]:pl-1 [&_ol>li]:mb-1.5 [&_ul>li]:mb-1.5"
                    data-testid={`support-faq-answer-${index}`}
                >
                    {item.a}
                </div>
            )}
        </div>
    );
}

export default function SupportPage() {
    const [openIndex, setOpenIndex] = useState(0);

    useEffect(() => {
        document.title = 'Support — BrainSync';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                'content',
                'Get help with BrainSync — the AI-powered second brain for your bookmarks. Contact support, FAQ, and quick answers to common questions.',
            );
        }
        if (typeof window !== 'undefined' && window.__brainsyncDismissSplash) {
            window.__brainsyncDismissSplash();
        }
    }, []);

    // FAQPage structured data — helps Google surface answers directly.
    const faqLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: typeof f.a === 'string' ? f.a : f.q,
            },
        })),
    };

    return (
        <main
            className="min-h-screen bg-[#FDFCF8] text-[#1F2D20]"
            data-testid="support-page"
        >
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
            />

            {/* Header */}
            <header className="px-6 pt-10 pb-6 sm:pt-16 max-w-3xl mx-auto">
                <Link
                    to="/"
                    className="text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:text-emerald-800"
                    data-testid="support-back-link"
                >
                    ← BrainSync
                </Link>
                <h1 className="mt-4 font-heading text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                    How can we help?
                </h1>
                <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
                    Most questions are answered below. If you need a human, we usually respond within one business day.
                </p>
            </header>

            {/* Contact card */}
            <section className="px-6 max-w-3xl mx-auto mb-12">
                <div
                    className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 via-white to-white p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5 justify-between"
                    data-testid="support-contact-card"
                >
                    <div className="flex items-center gap-4">
                        <span className="flex-shrink-0 w-11 h-11 rounded-full bg-emerald-700 text-white flex items-center justify-center">
                            <Mail className="w-5 h-5" />
                        </span>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                                Email support
                            </div>
                            <a
                                href="mailto:support@brainsync.pro"
                                className="font-heading text-lg sm:text-xl font-semibold text-zinc-900 hover:text-emerald-800 tracking-tight"
                                data-testid="support-email-link"
                            >
                                support@brainsync.pro
                            </a>
                            <div className="text-xs text-zinc-500 mt-0.5">
                                Typical reply: within 24 hours, Mon–Fri
                            </div>
                        </div>
                    </div>
                    <a href="mailto:support@brainsync.pro" data-testid="support-email-cta">
                        <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white gap-2 shadow-sm">
                            Send email <ArrowRight className="w-4 h-4" />
                        </Button>
                    </a>
                </div>
            </section>

            {/* Getting started */}
            <section className="px-6 max-w-3xl mx-auto mb-14" data-testid="support-getting-started">
                <h2 className="font-heading text-xl font-semibold tracking-tight text-zinc-900 mb-5">
                    Getting started in 60 seconds
                </h2>
                <ol className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                    <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">1</span>
                        <span><strong>Sign up free</strong> at <a href="https://brainsync.pro" className="text-emerald-700 hover:underline">brainsync.pro</a> — Google login or email, no credit card.</span>
                    </li>
                    <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">2</span>
                        <span><strong>Install the Chrome extension</strong> (or add the app to your Android home screen) so saving takes one click.</span>
                    </li>
                    <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">3</span>
                        <span><strong>Save your first link.</strong> BrainSync summarizes, tags, and files it automatically — usually done before you close the tab.</span>
                    </li>
                </ol>
                <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/how-it-works" data-testid="support-how-it-works">
                        <Button variant="outline" className="rounded-full border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                            See how it works
                        </Button>
                    </Link>
                    <Link to="/about" data-testid="support-about-link">
                        <Button variant="outline" className="rounded-full border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                            About BrainSync
                        </Button>
                    </Link>
                </div>
            </section>

            {/* FAQ */}
            <section className="px-6 max-w-3xl mx-auto mb-16" data-testid="support-faq">
                <h2 className="font-heading text-xl font-semibold tracking-tight text-zinc-900 mb-4">
                    Frequently asked
                </h2>
                <div className="rounded-2xl border border-zinc-200 bg-white/70 backdrop-blur px-5 sm:px-7 divide-y divide-zinc-100">
                    {FAQS.map((item, idx) => (
                        <FaqItem
                            key={idx}
                            item={item}
                            index={idx}
                            isOpen={openIndex === idx}
                            onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                        />
                    ))}
                </div>
            </section>

            {/* Bottom CTA + legal */}
            <section className="px-6 pb-16 max-w-3xl mx-auto" data-testid="support-footer-cta">
                <div className="rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-950 to-zinc-950 p-8 sm:p-10 text-white relative overflow-hidden">
                    <Sparkles className="absolute -right-4 -top-4 w-32 h-32 text-emerald-400/10" />
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                        Didn't find what you needed?
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-emerald-100/85 max-w-md">
                        Real humans read every message. Send us details, screenshots, or just say hi.
                    </p>
                    <a href="mailto:support@brainsync.pro" className="mt-6 inline-block" data-testid="support-bottom-cta">
                        <Button
                            size="lg"
                            className="rounded-full bg-white text-emerald-900 hover:bg-emerald-50 gap-2 shadow-lg"
                        >
                            Email support@brainsync.pro <ArrowRight className="w-4 h-4" />
                        </Button>
                    </a>
                </div>

                <div className="mt-8 text-center text-xs text-zinc-500 space-x-4" data-testid="support-legal-links">
                    <Link to="/privacy" className="hover:text-emerald-700 hover:underline">Privacy Policy</Link>
                    <span aria-hidden>·</span>
                    <Link to="/terms" className="hover:text-emerald-700 hover:underline">Terms of Service</Link>
                    <span aria-hidden>·</span>
                    <Link to="/about" className="hover:text-emerald-700 hover:underline">About</Link>
                </div>
                <p className="mt-4 text-center text-xs text-zinc-400">
                    brainsync.pro is operated by Aniral Ltd.
                </p>
            </section>
        </main>
    );
}
